/**
 * PURPOSE: Botón y diálogo de la importación SISDEU (FTR-0048..0050, 0052): "SISDEU" en el modal de acreditados
 *   (acreditados de una institución), "Instituciones SISDEU" en Instituciones de un deporte individual y "Traer todos
 *   los acreditados SISDEU" (acreditados de TODAS las instituciones participantes del deporte).
 * RESPONSIBILITIES:
 *   - Estado que decide el backend (useSisdeuImportStatus): oculto mientras carga o si la integración no está activa
 *     para el evento (compañía no habilitada, módulo apagado, rol sin permiso); deshabilitado con la explicación
 *     accionable del backend (institución no vinculada, evento no resuelto…); disponible si la acción existe.
 *   - Diálogo: "¿Qué deseas importar?" (● Solo aprobados por defecto / Todos) → [Cancelar] [Importar] (o [Vista previa]
 *     si la escritura está apagada) → "Importando…" sin poder cerrarse → resumen con detalle de resultados y
 *     advertencias, o el error del backend.
 *   - Avisa al contenedor mientras trabaja (onBusyChange) y tras importar (onImported) para refrescar listas, y si la
 *     integración está activa (onIntegrationActiveChange: el contenedor decide cómo mostrar los nombres SISDEU).
 *   - Variante masiva (kind BULK_ACCREDITATIONS, FTR-0052): el backend decide qué instituciones; el cuerpo solo lleva el
 *     alcance. Confirmación explícita antes de importar, sin doble envío, sin poder cerrar mientras escribe, y resumen
 *     de instituciones (participantes / procesadas / sin vínculo / con error) y de acreditados con su detalle.
 * COLLABORATORS: services/sisdeuImportService.js, hooks/useSisdeuImportStatus.js, utils/sisdeuName.js,
 *   ImportAccreditationsModal.jsx, InstitucionesArea.jsx.
 * ROUTE: winscore/src/area/InstitucionesArea/SisdeuImportPanel.jsx
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import { FiAlertTriangle, FiCheckCircle, FiDatabase, FiX } from "react-icons/fi";

import { useSisdeuImportStatus } from "../../hooks/useSisdeuImportStatus.js";
import {
  SISDEU_IMPORT_SCOPES,
  importSisdeuAccreditations,
  importSisdeuAllAccreditations,
  importSisdeuParticipants,
  previewSisdeuAccreditations,
  previewSisdeuAllAccreditations,
  previewSisdeuParticipants,
  sisdeuImportErrorMessage,
} from "../../services/sisdeuImportService.js";
import { formatSisdeuName } from "../../utils/sisdeuName.js";

export const SISDEU_PANEL_KINDS = Object.freeze({
  ACCREDITATIONS: "accreditations",
  PARTICIPANTS: "participants",
  BULK_ACCREDITATIONS: "bulkAccreditations",
});

export const WARNING_LABELS = Object.freeze({
  MISSING_DOCUMENT: "Sin documento",
  AMBIGUOUS_PERSON: "Persona ambigua",
  DOCTYPE_MISMATCH: "Tipo de documento distinto",
  DOCUMENT_NORMALIZED_MATCH: "Documento normalizado",
  DOCUMENT_CHANGED: "Documento cambiado en SISDEU",
  UNKNOWN_CATEGORY: "Categoría desconocida",
  DUPLICATE_JERSEY: "Camiseta repetida",
  INVALID_BIRTHDAY: "Fecha de nacimiento inválida",
  QUINCES_BIRTH_YEAR: "Fuera de edad (quinces)",
  DELETED_IN_WINSCORE: "Eliminada en Winscore",
  REASSIGNED_IN_SISDEU: "Reasignada en SISDEU",
  REPLACED_IN_SISDEU: "Reemplazada en SISDEU",
  NO_LONGER_APPROVED: "Ya no aprobada",
  NO_LONGER_IN_SCOPE: "Fuera del alcance",
  FIELD_DIFFERS: "Datos distintos en SISDEU",
});
const FIELD_LABELS = Object.freeze({ name: "nombre", function: "función", jersey: "camiseta", section: "sección" });

export const OUTCOME_LABELS = Object.freeze({
  CREATED: "Nueva",
  ADOPTED: "Existente vinculada",
  ALREADY_IMPORTED: "Ya importada",
  REPLACED_IN_SISDEU: "Reemplazada en SISDEU",
  DELETED_IN_WINSCORE: "Eliminada en Winscore",
  REASSIGNED_IN_SISDEU: "Reasignada en SISDEU",
  SKIPPED: "Omitida",
  EXCLUDED_BY_SCOPE: "Fuera del alcance elegido",
  DUPLICATE_COLLAPSED: "Duplicada en SISDEU",
  NO_LONGER_APPROVED: "Ya no aprobada",
  NO_LONGER_IN_SCOPE: "Fuera del alcance",
  OUTSIDE_SELECTED_SCOPE: "Importada, fuera del alcance elegido",
  EXISTING: "Ya existente",
  NOT_LINKED: "Sin vínculo",
  INVALID_INSTITUTION: "Institución inválida",
  REMOVED_IN_WINSCORE: "Retirada en Winscore",
  IMPORTED: "Procesada",
  FAILED: "Con error",
});

const plural = (count, singular, pluralText) => `${count} ${count === 1 ? singular : pluralText}`;

/** Líneas del resumen de acreditados (p. ej. "18 encontrados en SISDEU · 15 en el alcance · 13 ya importados…"). */
export const accreditationSummaryLines = (summary) => {
  if (!summary) return [];
  const a = summary.accreditations ?? {};
  const existing = (a.alreadyImported ?? 0) + (a.replacedInSisdeu ?? 0) + (a.adopted ?? 0);
  const lines = [
    `${summary.sisdeu?.found ?? 0} encontrados en SISDEU`,
    `${summary.sisdeu?.inScope ?? 0} en el alcance`,
    `${existing} ya importados/existentes`,
    plural(a.created ?? 0, "nuevo", "nuevos"),
  ];
  if (a.noLongerApproved) lines.push(plural(a.noLongerApproved, "ya no aprobado", "ya no aprobados"));
  if (a.noLongerInScope) lines.push(plural(a.noLongerInScope, "fuera del alcance", "fuera del alcance"));
  if (a.outsideSelectedScope) {
    lines.push(plural(a.outsideSelectedScope, "importado, fuera del alcance elegido", "importados, fuera del alcance elegido"));
  }
  if (a.deletedInWinscore) lines.push(plural(a.deletedInWinscore, "eliminado en Winscore (no se recrea)", "eliminados en Winscore (no se recrean)"));
  if (a.skipped) lines.push(plural(a.skipped, "omitido", "omitidos"));
  if (summary.categories) {
    lines.push(
      `${plural(summary.categories.catalogCreated ?? 0, "categoría nueva", "categorías nuevas")} · ${plural(summary.categories.entriesCreated ?? 0, "inscripción nueva", "inscripciones nuevas")}`
    );
  }
  return lines;
};

/** Resumen de INSTITUCIONES de la importación masiva (FTR-0052). */
export const bulkInstitutionLines = (summary) => {
  const i = summary?.institutions;
  if (!i) return [];
  const lines = [plural(i.participating ?? 0, "institución participante", "instituciones participantes"), plural(i.processed ?? 0, "procesada", "procesadas")];
  if (i.notLinked) lines.push(`${i.notLinked} sin vínculo SISDEU`);
  if (i.failed) lines.push(`${i.failed} con error`);
  return lines;
};

/** Resumen de ACREDITADOS de la masiva: los de una institución sumados + el total de advertencias. */
export const bulkAccreditationLines = (summary) => {
  const lines = accreditationSummaryLines(summary);
  const warnings = Object.values(summary?.warnings ?? {}).reduce((sum, value) => sum + (Number(value) || 0), 0);
  if (warnings) lines.push(plural(warnings, "advertencia", "advertencias"));
  return lines;
};

export const participantsSummaryLines = (summary) => {
  if (!summary) return [];
  const i = summary.institutions ?? {};
  const lines = [
    `${summary.sisdeu?.found ?? 0} instituciones en SISDEU`,
    `${summary.sisdeu?.inScope ?? 0} en el alcance`,
    plural(i.created ?? 0, "agregada", "agregadas"),
    plural(i.existing ?? 0, "ya existente", "ya existentes"),
  ];
  if (i.notLinked) lines.push(plural(i.notLinked, "sin vínculo", "sin vínculo"));
  if (i.invalid) lines.push(plural(i.invalid, "inválida", "inválidas"));
  if (i.removedInWinscore) lines.push(plural(i.removedInWinscore, "retirada en Winscore", "retiradas en Winscore"));
  return lines;
};

export const RECONCILIATION_RESULT_LABELS = Object.freeze({
  IMPORTED: "Importado",
  ALREADY_EXISTS: "Ya existía",
  REACTIVATED: "Reactivado",
  FAILED: "No importado",
});

/**
 * Líneas de la ALERTA DE IMPORTACIÓN (backend: result.reconciliation). Solo existe cuando hay discrepancia: acreditados
 * SISDEU válidos que no quedaron reflejados en Winscore o un deporte que no pudo verificarse contra SISDEU.
 */
export const reconciliationAlertLines = (reconciliation, { scope, mode } = {}) => {
  if (!reconciliation || reconciliation.ok) return [];
  const c = reconciliation.counts ?? {};
  const scopeLabel = scope === "ALL" ? "Todos" : "Solo aprobados";
  const preview = mode === "PREVIEW";
  const lines = [
    plural(c.sisdeuFound ?? 0, "acreditado encontrado en SISDEU", "acreditados encontrados en SISDEU") + ".",
    `${plural(c.valid ?? 0, "cumple", "cumplen")} el filtro ${scopeLabel}.`,
    `${plural(c.reflected ?? 0, preview ? "quedaría reflejado" : "quedó reflejado", preview ? "quedarían reflejados" : "quedaron reflejados")} en Winscore.`,
  ];
  const missing = (c.failed ?? 0) + (reconciliation.unexplained ?? 0);
  if (missing) {
    lines.push(`${plural(missing, "acreditado válido NO pudo importarse", "acreditados válidos NO pudieron importarse")}.`);
  }
  if (reconciliation.pairCheck === "UNAVAILABLE") {
    lines.push("No se pudo verificar el deporte completo contra SISDEU: vuelve a ejecutar la importación para comprobarlo.");
  }
  return lines;
};

const warningText = (warning) => {
  const label = WARNING_LABELS[warning.code] || warning.code;
  return Array.isArray(warning.fields) && warning.fields.length
    ? `${label} (${warning.fields.map((field) => FIELD_LABELS[field] || field).join(", ")})`
    : label;
};

/** Fila de detalle de la masiva: una institución con su resultado, mensaje, conteos y advertencias (sin ids SISDEU). */
const BulkInstitutionDetail = ({ row }) => {
  const summary = row.summary;
  const warnings = Object.entries(summary?.warnings ?? {});
  const a = summary?.accreditations ?? {};
  const existing = (a.alreadyImported ?? 0) + (a.replacedInSisdeu ?? 0) + (a.adopted ?? 0);
  return (
    <li className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">{row.name || row.abrev || "Institución"}</span>
        {row.abrev && row.name ? <span className="rounded-full bg-slate-100 px-2 py-0.5">{row.abrev}</span> : null}
        <span
          className={`rounded-full px-2 py-0.5 font-semibold ${row.outcome === "IMPORTED" ? "bg-indigo-50 text-indigo-700" : "bg-amber-100 text-amber-800"}`}
        >
          {OUTCOME_LABELS[row.outcome] || row.outcome}
        </span>
      </div>
      {row.message ? <p className="mt-1 text-amber-700">{row.message}</p> : null}
      {summary ? (
        <p className="mt-1 text-slate-500">
          {[
            `${summary.sisdeu?.found ?? 0} encontrados`,
            `${summary.sisdeu?.inScope ?? 0} en el alcance`,
            `${existing} ya existentes`,
            plural(a.created ?? 0, "nuevo", "nuevos"),
          ].join(" · ")}
        </p>
      ) : null}
      {warnings.length ? (
        <p className="mt-1 text-amber-700">{warnings.map(([code, count]) => `${WARNING_LABELS[code] || code}: ${count}`).join(" · ")}</p>
      ) : null}
    </li>
  );
};

const SisdeuImportPanel = ({
  kind = SISDEU_PANEL_KINDS.ACCREDITATIONS,
  idevent,
  idsport,
  institution = null,
  label = null,
  disabled = false,
  participantsCount = null,
  showMessage = true,
  onImported,
  onBusyChange,
  onIntegrationActiveChange,
}) => {
  const isParticipants = kind === SISDEU_PANEL_KINDS.PARTICIPANTS;
  const isBulk = kind === SISDEU_PANEL_KINDS.BULK_ACCREDITATIONS;
  // Participantes y masiva son acciones del evento/deporte; las de acreditados van por institución.
  const sportLevel = isParticipants || isBulk;
  const idinstitution = sportLevel ? null : institution?.idinstitution ?? null;
  const { loading, status, reload } = useSisdeuImportStatus({
    idevent,
    idsport,
    idinstitution,
    enabled: sportLevel || Boolean(idinstitution),
  });

  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(SISDEU_IMPORT_SCOPES.APPROVED);
  const [step, setStep] = useState("select");
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const [showFailures, setShowFailures] = useState(false);

  const inFlight = useRef(false);

  const canPreview = isBulk
    ? status.actions.previewAllAccreditations
    : isParticipants
      ? status.actions.previewParticipants
      : status.actions.previewAccreditations;
  const canApply = isBulk
    ? status.actions.applyAllAccreditations
    : isParticipants
      ? status.actions.applyParticipants
      : status.actions.applyAccreditations;
  const running = step === "running";

  useEffect(() => {
    onBusyChange?.(running);
  }, [running, onBusyChange]);

  const integrationActive = !loading && status.integrationActive;
  useEffect(() => {
    onIntegrationActiveChange?.(integrationActive);
  }, [integrationActive, onIntegrationActiveChange]);

  const summaryLines = useMemo(
    () => (isParticipants ? participantsSummaryLines(result?.summary) : isBulk ? bulkAccreditationLines(result?.summary) : accreditationSummaryLines(result?.summary)),
    [isParticipants, isBulk, result]
  );
  const institutionLines = useMemo(() => (isBulk ? bulkInstitutionLines(result?.summary) : []), [isBulk, result]);
  const warningCounts = Object.entries(result?.summary?.warnings ?? {});
  const detailRows = (isBulk ? result?.institutions : result?.rows) ?? [];
  const bulkProblems = isBulk ? (result?.summary?.institutions?.notLinked ?? 0) + (result?.summary?.institutions?.failed ?? 0) : 0;

  // Oculto: sin confirmación del backend o integración inactiva para este evento (company 2, módulo apagado…). Un
  // diálogo ya abierto se conserva aunque el estado cambie, para no perder el resultado mostrado.
  const hidden = loading || !status.integrationActive;
  if (hidden && !open) return null;

  const buttonLabel = label || (isBulk ? "Traer todos los acreditados SISDEU" : isParticipants ? "Instituciones SISDEU" : "SISDEU");
  // La masiva solo necesita su propia acción (no `enabled`: una configuración cerrada no impide importar acreditados).
  const available = isBulk ? Boolean(canPreview) : status.enabled && canPreview;

  const openDialog = () => {
    setScope(SISDEU_IMPORT_SCOPES.APPROVED);
    setStep("select");
    setResult(null);
    setErrorMessage("");
    setShowDetail(false);
    setShowFailures(false);
    setOpen(true);
  };

  const closeDialog = () => {
    if (running) return;
    setOpen(false);
  };

  const run = async () => {
    // Un segundo clic antes de que se pinte "Importando…" no lanza otra importación.
    if (inFlight.current) return;
    inFlight.current = true;
    const apply = canApply;
    setStep("running");
    setErrorMessage("");
    try {
      const args = { idevent, idsport, idinstitution, scope };
      let data;
      if (isBulk) data = await (apply ? importSisdeuAllAccreditations(args) : previewSisdeuAllAccreditations(args));
      else if (isParticipants) data = await (apply ? importSisdeuParticipants(args) : previewSisdeuParticipants(args));
      else data = await (apply ? importSisdeuAccreditations(args) : previewSisdeuAccreditations(args));
      setResult(data);
      setStep("result");
      if (apply) {
        reload();
        onImported?.(data);
      }
    } catch (error) {
      setErrorMessage(sisdeuImportErrorMessage(error));
      setStep("error");
    } finally {
      inFlight.current = false;
    }
  };

  const reconciliation = result?.reconciliation ?? null;
  const discrepancy = Boolean(reconciliation) && !reconciliation.ok;
  const alertLines = reconciliationAlertLines(reconciliation, { scope: result?.scope, mode: result?.mode });
  const failures = reconciliation?.failures ?? [];
  const resolvedByAbrev = isBulk
    ? (result?.institutions ?? []).filter((row) => row.institutionSource === "INSTITUTION_ABREV_EXACT")
    : result?.institutionSource === "INSTITUTION_ABREV_EXACT"
      ? [{ abrev: institution?.abrev, name: institution?.name }]
      : [];
  const hasWarnings = warningCounts.length > 0 || bulkProblems > 0 || discrepancy;

  return (
    <>
      {!hidden && (
      <div className="inline-flex flex-col">
        <button
          type="button"
          onClick={openDialog}
          disabled={!available || disabled}
          title={
            available
              ? isBulk
                ? "Importar los acreditados SISDEU de todas las instituciones participantes"
                : "Importar desde SISDEU"
              : status.message || "Importación SISDEU no disponible"
          }
          className="inline-flex items-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiDatabase className="h-4 w-4" />
          {buttonLabel}
        </button>
        {!available && status.message && showMessage ? (
          <span role="note" className="mt-1 max-w-xs text-[11px] font-medium text-amber-700">
            {status.message}
          </span>
        ) : null}
      </div>
      )}

      <Modal open={open} onClose={closeDialog}>
        <ModalDialog
          layout="center"
          aria-labelledby="sisdeu-import-title"
          sx={{ p: 0, border: "none", background: "transparent", boxShadow: "none", width: "min(720px, calc(100vw - 24px))" }}
        >
          <div className="flex max-h-[calc(100dvh-24px)] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="sisdeu-import-title" className="text-lg font-bold text-slate-900">
                  {isBulk ? "Importar acreditados SISDEU" : isParticipants ? "Instituciones SISDEU" : "Importar desde SISDEU"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {isBulk
                    ? "Se importarán los acreditados SISDEU de todas las instituciones participantes de este deporte."
                    : isParticipants
                      ? "Agrega como participantes las instituciones vinculadas que tienen acreditados en SISDEU."
                      : `Acreditados SISDEU de ${institution?.name || institution?.abrev || "la institución"}.`}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                disabled={running}
                aria-label="Cerrar"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {step === "select" && isBulk ? (
                <div className="mb-4 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-xs text-indigo-900" data-testid="sisdeu-bulk-intro">
                  {Number.isInteger(participantsCount) ? (
                    <p className="font-semibold">
                      {participantsCount === 1 ? "Se encontró 1 institución participante." : `Se encontraron ${participantsCount} instituciones participantes.`}
                    </p>
                  ) : null}
                  <p className={Number.isInteger(participantsCount) ? "mt-1" : ""}>
                    Se procesarán varias instituciones, una por una. Las que no tengan vínculo SISDEU se omitirán y aparecerán en el resumen final.
                  </p>
                </div>
              ) : null}

              {step === "select" ? (
                <fieldset>
                  <legend className="text-sm font-bold text-slate-800">¿Qué deseas importar?</legend>
                  <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="radio"
                      name="sisdeu-scope"
                      value={SISDEU_IMPORT_SCOPES.APPROVED}
                      checked={scope === SISDEU_IMPORT_SCOPES.APPROVED}
                      onChange={() => setScope(SISDEU_IMPORT_SCOPES.APPROVED)}
                    />
                    Solo aprobados
                  </label>
                  <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="radio"
                      name="sisdeu-scope"
                      value={SISDEU_IMPORT_SCOPES.ALL}
                      checked={scope === SISDEU_IMPORT_SCOPES.ALL}
                      onChange={() => setScope(SISDEU_IMPORT_SCOPES.ALL)}
                    />
                    Todos
                  </label>
                  <p className="mt-3 text-xs text-slate-500">
                    "Todos" incluye borradores, revisión, subsanar y aprobación parcial, con su estado SISDEU real; nunca anulados ni rechazados.
                  </p>
                  {!canApply ? (
                    <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                      Modo vista previa: se muestra el resultado sin escribir nada en Winscore.
                    </p>
                  ) : null}
                </fieldset>
              ) : null}

              {running ? (
                <div className="py-8 text-center text-sm font-semibold text-slate-600" role="status">
                  {isBulk
                    ? canApply
                      ? "Importando acreditados SISDEU..."
                      : "Consultando acreditados SISDEU..."
                    : canApply
                      ? "Importando..."
                      : "Consultando..."}
                </div>
              ) : null}

              {step === "error" ? (
                <div role="alert" className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              ) : null}

              {step === "result" && result ? (
                <div>
                  <div
                    className={`flex items-start gap-2 rounded-2xl border px-4 py-3 text-sm ${hasWarnings ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}
                  >
                    {hasWarnings ? <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : <FiCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />}
                    <div>
                      {result.mode === "PREVIEW" ? <p className="mb-1 text-xs font-bold uppercase">Vista previa: no se escribió nada</p> : null}
                      {isBulk && result.mode !== "PREVIEW" ? (
                        <p className="mb-1 font-bold">{discrepancy ? "Importación SISDEU con discrepancias" : "Importación SISDEU completada"}</p>
                      ) : null}
                      {isBulk ? <p className="mt-1 text-xs font-bold uppercase tracking-wide">Instituciones</p> : null}
                      {isBulk ? (
                        <ul data-testid="sisdeu-summary-institutions">
                          {institutionLines.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                      {isBulk ? <p className="mt-2 text-xs font-bold uppercase tracking-wide">Acreditados</p> : null}
                      <ul data-testid="sisdeu-summary">
                        {summaryLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {reconciliation && !discrepancy ? (
                    <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid="sisdeu-reconciliation-ok">
                      Reconciliación: {plural(reconciliation.counts?.reflected ?? 0, "acreditado reflejado", "acreditados reflejados")} de {reconciliation.counts?.valid ?? 0} válidos en SISDEU.
                    </p>
                  ) : null}

                  {resolvedByAbrev.length ? (
                    <p className="mt-3 rounded-xl bg-indigo-50 px-3 py-2 text-xs text-indigo-900" data-testid="sisdeu-abrev-note">
                      {plural(resolvedByAbrev.length, "institución se resolvió", "instituciones se resolvieron")} en SISDEU por su sigla exacta (sin vínculo explícito):{" "}
                      {resolvedByAbrev.map((row) => row.abrev || row.name).join(", ")}.
                    </p>
                  ) : null}

                  {discrepancy ? (
                    <div role="alert" className="mt-3 rounded-2xl border-2 border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900" data-testid="sisdeu-reconciliation-alert">
                      <p className="font-black uppercase tracking-wide">⚠️ ALERTA DE IMPORTACIÓN</p>
                      <ul className="mt-2 space-y-0.5">
                        {alertLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                      {reconciliation.unresolvedInstitutions?.length ? (
                        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs" aria-label="Instituciones sin resolver">
                          {reconciliation.unresolvedInstitutions.map((item, index) => (
                            <li key={`${item.code}-${index}`}>
                              <span className="font-semibold">{item.sisdeuInstitution?.name || item.sisdeuInstitution?.abrev || "Institución SISDEU"}</span>
                              {item.sisdeuInstitution?.abrev && item.sisdeuInstitution?.name ? ` (${item.sisdeuInstitution.abrev})` : ""}: {plural(item.accreditations ?? 0, "acreditado", "acreditados")}. {item.message}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {failures.length ? (
                        <button
                          type="button"
                          onClick={() => setShowFailures((value) => !value)}
                          className="mt-2 text-sm font-semibold text-red-800 underline"
                        >
                          {showFailures ? "Ocultar detalle de los no importados" : `Ver detalle de los no importados (${failures.length})`}
                        </button>
                      ) : null}
                      {showFailures ? (
                        <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto pr-1" aria-label="Acreditados no importados">
                          {failures.map((failure, index) => (
                            <li key={`${failure.code}-${failure.document}-${index}`} className="rounded-xl border border-red-200 bg-white px-3 py-2 text-xs text-slate-800">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-slate-900">{formatSisdeuName(failure.name) || "Sin nombre"}</span>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5">Documento: {failure.document || "—"}</span>
                                {failure.state ? <span className="rounded-full bg-slate-100 px-2 py-0.5">{failure.state}</span> : null}
                                <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-800">{RECONCILIATION_RESULT_LABELS.FAILED}</span>
                              </div>
                              <p className="mt-1 text-slate-600">
                                Institución: {failure.institution?.name || failure.institution?.abrev || "Sin institución Winscore"}
                                {failure.sisdeuInstitution?.name || failure.sisdeuInstitution?.abrev
                                  ? ` · SISDEU: ${failure.sisdeuInstitution.name || failure.sisdeuInstitution.abrev}`
                                  : ""}
                              </p>
                              <p className="mt-1 text-red-800">
                                {failure.reason} <span className="font-mono text-[11px]">({failure.code})</span>
                              </p>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}

                  {warningCounts.length > 0 ? (
                    <ul className="mt-3 flex flex-wrap gap-2" aria-label="Advertencias">
                      {warningCounts.map(([code, count]) => (
                        <li key={code} className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
                          {WARNING_LABELS[code] || code}: {count}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {detailRows.length ? (
                    <button
                      type="button"
                      onClick={() => setShowDetail((value) => !value)}
                      className="mt-3 text-sm font-semibold text-indigo-700 hover:underline"
                    >
                      {showDetail ? "Ocultar detalle" : "Ver detalle"}
                    </button>
                  ) : null}

                  {showDetail ? (
                    <ul className="mt-2 max-h-72 space-y-2 overflow-y-auto pr-1" aria-label="Detalle de la importación">
                      {detailRows.map((row, index) =>
                        isBulk ? (
                          <BulkInstitutionDetail key={`${row.idinstitution}-${index}`} row={row} />
                        ) : (
                        <li key={`${row.outcome}-${row.name}-${index}`} className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700">
                          <div className="flex flex-wrap items-center gap-2">
                            {/* Acreditados: nombre de persona de SISDEU (presentación Title Case); instituciones: tal cual. */}
                            <span className="font-semibold text-slate-900">{(isParticipants ? row.name : formatSisdeuName(row.name)) || row.abrev || "Sin nombre"}</span>
                            {row.function ? <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">{row.function}</span> : null}
                            {row.state ? <span className="rounded-full bg-slate-100 px-2 py-0.5">{row.state}</span> : null}
                            <span className="rounded-full bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-700">{OUTCOME_LABELS[row.outcome] || row.outcome}</span>
                          </div>
                          {row.message ? <p className="mt-1 text-amber-700">{row.message}</p> : null}
                          {row.warnings?.length ? <p className="mt-1 text-amber-700">{row.warnings.map(warningText).join(" · ")}</p> : null}
                          {row.categories?.length ? <p className="mt-1 text-slate-500">{row.categories.join(" · ")}</p> : null}
                        </li>
                        )
                      )}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={step === "error" ? () => setStep("select") : closeDialog}
                disabled={running}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {step === "select" ? "Cancelar" : step === "error" ? "Volver" : "Cerrar"}
              </button>
              {step === "select" || running ? (
                <button
                  type="button"
                  onClick={run}
                  disabled={running}
                  className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                >
                  {running ? (canApply ? "Importando..." : "Consultando...") : canApply ? (isBulk ? "Traer acreditados" : "Importar") : "Vista previa"}
                </button>
              ) : null}
            </div>
          </div>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default SisdeuImportPanel;
